import { APIData }                                      from "types/cms/frontendTypes";
import isEqual                                          from 'lodash/isEqual';
import { CreateRecoRequestData, 
         QA_WORKFLOW, 
         RECO_VERIFICATION_STATUS,
         WORKFLOW_STATE,
         CONTENT_EDIT_ACTION
       }                                                from "types/apis/recoInterfaces";
import { RunContextApp }                                from "framework/RCApp";

export const RecoUtils = {

  getRecoDiff : (newReco : CreateRecoRequestData, 
                 oldReco : Partial<APIData<'api::recommendation.recommendation'>>) => {
  
    const diff : Partial<APIData<'api::recommendation.recommendation'>> | Partial<CreateRecoRequestData> = {};
    
    // Classification
    if (!isEqual(newReco.disabled, oldReco.disabled)) diff.disabled = newReco.disabled;
    if (!isEqual(newReco.fuelType, oldReco.fuelType)) diff.fuelType = newReco.fuelType;
    if (!isEqual(newReco.category, oldReco.category)) diff.category = newReco.category;
    
    if (!isEqual(newReco.frequency, oldReco.frequency)) diff.frequency = newReco.frequency;
    if (!isEqual(newReco.actionSavingsPotential, oldReco.actionSavingsPotential)) diff.actionSavingsPotential = newReco.actionSavingsPotential;
    if (!isEqual(newReco.userType, oldReco.userType)) diff.userType = newReco.userType;
    if (!isEqual(newReco.actionType, oldReco.actionType)) diff.actionType = newReco.actionType;
    if (!isEqual(newReco.objective, oldReco.objective)) diff.objective = newReco.objective;

    // Content
    if (!isEqual(newReco.title, oldReco.title)) diff.title = newReco.title;
    if (!isEqual(newReco.description, oldReco.description)) diff.description = newReco.description;
    if (!isEqual(newReco.detail, oldReco.detail)) diff.detail = newReco.detail;
    if (!isEqual(newReco.ctaButtonText, oldReco.ctaButtonText)) diff.ctaButtonText = newReco.ctaButtonText;
    if (!isEqual(newReco.ctaButtonLink, oldReco.ctaButtonLink)) diff.ctaButtonLink = newReco.ctaButtonLink;
    if (!isEqual(newReco.videoLink, oldReco.videoLink)) diff.videoLink = newReco.videoLink;
    if (!isEqual(newReco.featuredContent, oldReco.featuredContent)) diff.featuredContent = newReco.featuredContent;
    if (!isEqual(newReco.program, oldReco.program)) diff.program = newReco.program;
    if (!isEqual(newReco.channelPaper, oldReco.channelPaper)) diff.channelPaper = newReco.channelPaper;
    if (!isEqual(newReco.channelEmail, oldReco.channelEmail)) diff.channelEmail = newReco.channelEmail;
    if (!isEqual(newReco.channelWeb, oldReco.channelWeb)) diff.channelWeb = newReco.channelWeb;
    if (!isEqual(newReco.localizations, oldReco.localizations)) diff.localizations = newReco.localizations;
    
    // Images
    if ((typeof newReco.icon === 'number') || 
        (typeof newReco.icon === 'object' && newReco.icon?.id !== oldReco.icon?.id)) diff.icon = newReco.icon;

    if ((typeof newReco.elongatedImage === 'number') || 
        (typeof newReco.elongatedImage === 'object' && newReco.elongatedImage?.id !== oldReco.elongatedImage?.id)) diff.elongatedImage = newReco.elongatedImage;

    if ((typeof newReco.fullImage === 'number') || 
        (typeof newReco.fullImage === 'object' && newReco.fullImage?.id !== oldReco.fullImage?.id)) diff.fullImage = newReco.fullImage;

    if ((typeof newReco.shortenedImage === 'number') || 
        (typeof newReco.shortenedImage === 'object' && newReco.shortenedImage?.id !== oldReco.shortenedImage?.id)) diff.shortenedImage = newReco.shortenedImage;
    
    if ((typeof newReco.herImage === 'number') || 
        (typeof newReco.herImage === 'object' && newReco.herImage?.id !== oldReco.herImage?.id)) diff.herImage = newReco.herImage;
    
    // Score
    if (!isEqual(newReco.score, oldReco.score)) diff.score = newReco.score;
    
    // Exclusions
    if (!isEqual(newReco.homeProfileExclusion, oldReco.homeProfileExclusion)) diff.homeProfileExclusion = newReco.homeProfileExclusion;
    if (!isEqual(newReco.applianceProfileExclusion, oldReco.applianceProfileExclusion)) diff.applianceProfileExclusion = newReco.applianceProfileExclusion;
    if (!isEqual(newReco.ratePlanExclusion, oldReco.ratePlanExclusion)) diff.ratePlanExclusion = newReco.ratePlanExclusion;
    if (!isEqual(newReco.ppExclusion, oldReco.ppExclusion)) diff.ppExclusion = newReco.ppExclusion;
    
    // Associations
    if (!isEqual(newReco.insightAssociation, oldReco.insightAssociation)) diff.insightAssociation = newReco.insightAssociation;
    
    // Verification Status (only for existing recommendations, not for creation)
    if ('verificationStatus' in newReco && !isEqual(newReco.verificationStatus, oldReco.verificationStatus)) {
      (diff as any).verificationStatus = newReco.verificationStatus;
    }

    return diff
  },

  computeRecoRanksForCategory : (categoryId : string, 
                                 recos : APIData<'api::recommendation.recommendation'>[], 
                                 unsavedRecos : Record<string, Partial<APIData<'api::recommendation.recommendation'>>>,
                                 recoScore : APIData<'api::utility-reco-score.utility-reco-score'>) 
    : Record<string, number> => {
    
    // Filter all recos that are in the category
    const categoryRecos = recos.filter((reco) => reco.category === categoryId)
    categoryRecos.sort((a, b) => a.recommendationId.localeCompare(b.recommendationId))

    // Filter all unsaved recos that have recommendationId within the list categoryRecos
    const unsavedCategoryRecos = Object.values(unsavedRecos).filter((reco) => 
      categoryRecos.some((r) => r.recommendationId === reco.recommendationId))

    let premScores : Record<string, number> = {}
    categoryRecos.forEach((reco) => {
        const score = (reco.score || {}) as any
        premScores[reco.recommendationId] = score.impact + score.simplicity
    })

    unsavedCategoryRecos.forEach((reco) => {
        const score = (reco.score || {}) as any
        premScores[reco.recommendationId!] = score.impact + score.simplicity
    })

    // create an array of sorted prem scores and their reco ids
    const sortedPremScores = Object.entries(premScores).sort((a, b) => b[1] - a[1])

    // get the pilot reco score threshold for the category
    const pilotScoreThreshold = recoScore.thresholds as Record<string, {
        max : number
        min : number
    }>
    const max = pilotScoreThreshold[categoryId].max
    const min = pilotScoreThreshold[categoryId].min
    const step = (max - min) / categoryRecos.length

    const ranks : Record<string, number> = {}

    let sub = 0
    for (let i = 0; i < sortedPremScores.length; i++) {
        const [recoId, premScore] = sortedPremScores[i]
        const rank = max - sub
        ranks[recoId] = rank
        sub += step
    }
    return ranks
  },

  getNextWorkflowState : (rc : RunContextApp,
                          currentQaStatus : RECO_VERIFICATION_STATUS, 
                          reviewWorkflow : QA_WORKFLOW) : WORKFLOW_STATE => {
    
   const userRole = rc.permissionsManager.getUserRole()

    // Filter edges for user role
    const filteredWorkflow = reviewWorkflow.filter((wf) => {
      return wf.userRoles.includes(userRole)
    })

    if (!filteredWorkflow || filteredWorkflow.length === 0) {
      throw new Error(`No workflow found for user role ${userRole}`)
    }

    const workflow = filteredWorkflow[0]
    const actions = workflow.actions[currentQaStatus]

    const edges = workflow.qaWorkflowEdges

    const graph = {} as Record<RECO_VERIFICATION_STATUS, RECO_VERIFICATION_STATUS[]>

    for (const edge of edges) {
      const from = edge.from
      const to = edge.to
      
      if (!graph[from]) {
        graph[from] = []
      }

      graph[from].push(to)
    }

    return {
      nextQaStatus : graph[currentQaStatus] || [],
      actions : actions
    }
  },

  // New utility functions for workflow integration
  getWorkflowStateForReco : (currentQaStatus : RECO_VERIFICATION_STATUS,
                             reviewWorkflow : QA_WORKFLOW,
                             rc : RunContextApp) : WORKFLOW_STATE => {

    return RecoUtils.getNextWorkflowState(rc, currentQaStatus, reviewWorkflow)
  },

  isActionAllowed : (action : CONTENT_EDIT_ACTION, 
                     workflowState : WORKFLOW_STATE) : boolean => {
    
    return workflowState.actions.includes(action)
  }
}
